import React, { useState, useEffect } from 'react';
import { FaInstagram, FaHeart, FaComment, FaExternalLinkAlt, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { API_BASE } from '../config';
import './InstagramSection.css';

const InstagramSection = () => {
  const [feeds, setFeeds] = useState([]);
  const [activeFeed, setActiveFeed] = useState(0);
  const [postsMap, setPostsMap] = useState({});
  const [highlightsMap, setHighlightsMap] = useState({});
  const [storyViewer, setStoryViewer] = useState(null); // { stories, index }

  useEffect(() => {
    fetch(`${API_BASE}/instagram-feeds`)
      .then(res => res.json())
      .then(data => {
        setFeeds(data);
        data.forEach(feed => {
          fetch(`${API_BASE}/instagram-feeds/${feed._id}/posts`)
            .then(res => res.json())
            .then(posts => {
              setPostsMap(prev => ({ ...prev, [feed._id]: posts }));
            })
            .catch(() => {});

          fetch(`${API_BASE}/instagram-feeds/${feed._id}/highlights`)
            .then(res => res.json())
            .then(highlights => {
              setHighlightsMap(prev => ({ ...prev, [feed._id]: highlights }));
            })
            .catch(() => {});
        });
      })
      .catch(() => {});
  }, []);

  if (feeds.length === 0) return null;

  const current = feeds[activeFeed];
  const currentPosts = postsMap[current?._id] || [];
  const currentHighlights = highlightsMap[current?._id] || [];

  const openStory = (highlight) => {
    if (highlight.stories && highlight.stories.length > 0) {
      setStoryViewer({ stories: highlight.stories, index: 0, title: highlight.title });
    }
  };

  const closeStory = () => setStoryViewer(null);

  const prevStory = () => {
    if (storyViewer && storyViewer.index > 0) {
      setStoryViewer({ ...storyViewer, index: storyViewer.index - 1 });
    }
  };

  const nextStory = () => {
    if (storyViewer && storyViewer.index < storyViewer.stories.length - 1) {
      setStoryViewer({ ...storyViewer, index: storyViewer.index + 1 });
    } else {
      closeStory();
    }
  };

  return (
    <section className="instagram-section">
      <div className="instagram-container">
        <div className="instagram-header">
          <div className="instagram-header-left">
            <span className="instagram-subtitle">
              <FaInstagram /> Instagram
            </span>
            <h2 className="instagram-title">Bizi Takip Edin</h2>
          </div>
        </div>

        {/* Tesis Tabları */}
        <div className="instagram-facility-tabs">
          {feeds.map((feed, index) => (
            <button
              className={`instagram-facility-tab ${activeFeed === index ? 'active' : ''}`}
              key={feed._id}
              onClick={() => setActiveFeed(index)}
            >
              {feed.facilityName}
            </button>
          ))}
        </div>

        {/* Profil Bilgisi */}
        <div className="instagram-profile">
          <FaInstagram className="instagram-profile-icon" />
          <span className="instagram-handle">{current.handle}</span>
          {current.link && (
            <a href={current.link} className="instagram-follow-btn" target="_blank" rel="noopener noreferrer">
              Takip Et <FaExternalLinkAlt />
            </a>
          )}
        </div>

        {/* Highlights */}
        {currentHighlights.length > 0 && (
          <div className="instagram-highlights">
            {currentHighlights.map((hl) => (
              <button
                className="instagram-highlight"
                key={hl.id}
                onClick={() => openStory(hl)}
              >
                <div className="instagram-highlight-ring">
                  <img src={hl.cover} alt={hl.title} />
                </div>
                <span className="instagram-highlight-title">{hl.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* Post Grid */}
        {currentPosts.length > 0 ? (
          <div className="instagram-grid">
            {currentPosts.slice(0, 8).map((post, index) => (
              <a
                href={post.permalink || current.link || '#'}
                className="instagram-post"
                key={post.id || index}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={post.image} alt={post.caption || 'Instagram post'} />
                <div className="instagram-post-overlay">
                  {post.likes != null && <span><FaHeart /> {post.likes}</span>}
                  {post.comments != null && <span><FaComment /> {post.comments}</span>}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="instagram-empty">
            <FaInstagram />
            <p>Henüz paylaşım yok</p>
          </div>
        )}
      </div>

      {/* Story Viewer Modal */}
      {storyViewer && (
        <div className="story-viewer-overlay" onClick={closeStory}>
          <div className="story-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="story-viewer-header">
              <span className="story-viewer-title">{storyViewer.title}</span>
              <span className="story-viewer-counter">
                {storyViewer.index + 1} / {storyViewer.stories.length}
              </span>
              <button className="story-viewer-close" onClick={closeStory}>
                <FaTimes />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="story-viewer-progress">
              {storyViewer.stories.map((_, i) => (
                <div
                  key={i}
                  className={`story-progress-bar ${i <= storyViewer.index ? 'active' : ''}`}
                />
              ))}
            </div>

            <div className="story-viewer-content">
              {storyViewer.stories[storyViewer.index].video ? (
                <video
                  src={storyViewer.stories[storyViewer.index].video}
                  autoPlay
                  playsInline
                  muted
                  onEnded={nextStory}
                />
              ) : (
                <img
                  src={storyViewer.stories[storyViewer.index].image}
                  alt="Story"
                />
              )}
            </div>

            {storyViewer.index > 0 && (
              <button className="story-nav story-nav--prev" onClick={prevStory}>
                <FaChevronLeft />
              </button>
            )}
            {storyViewer.index < storyViewer.stories.length - 1 && (
              <button className="story-nav story-nav--next" onClick={nextStory}>
                <FaChevronRight />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default InstagramSection;
